// OutlineReviewView — DEBT-19BI-EXTRACT-CONFIRM-STEP
//
// User-facing outline review. Shows what the AI extracted, lets the user
// quickly confirm or edit, then proceed to generation.
//
// Design:
//   - Shortest path: "Looks good → confirm" in one click
//   - Non-technical language only in default view
//   - Advanced editing (Bloom, quota, mustCover) behind a collapsible section
//   - Step indicator showing progress in the creation flow
//   - User feedback option for reporting extraction issues
//
// Session 48: business logic extracted to useOutlineEditor hook.

import {  useState } from 'react';
import {
  Pencil, CheckCircle2, AlertCircle, ArrowRight,
} from 'lucide-react';
import { useT } from '../i18n/index.js';
import { useOutlineKeyboard } from '../lib/hooks/use-outline-keyboard.js';
import {
  useOutlineEditor,
  type FeedbackIssue,
} from '../lib/hooks/use-outline-editor.js';
import { ChapterCard, type Labels } from '../components/ChapterCard.js';
import type { CurriculumOutline } from '@examforge/shared/schemas';
import { useToast } from '../components/toast.js';
import type { DictionaryKey } from '@examforge/shared/i18n';

// ─── Props ──────────────────────────────────────────────────────────────────

interface OutlineReviewProps {
  outline: CurriculumOutline;
  courseTitle: string;
  onConfirm: (outline: CurriculumOutline) => void;
  onCancel: () => void;
  isConfirming?: boolean;
}

// ─── Importance config ──────────────────────────────────────────────────────
// Label + desc come from the i18n dictionary; only the numeric value is static.

const IMPORTANCE_KEYS: ReadonlyArray<{ value: 1 | 2 | 3; label: DictionaryKey; desc: DictionaryKey }> = [
  { value: 1, label: 'outlineReview.importance.overview', desc: 'outlineReview.importance.overviewDesc' },
  { value: 2, label: 'outlineReview.importance.understand', desc: 'outlineReview.importance.understandDesc' },
  { value: 3, label: 'outlineReview.importance.keyTopic', desc: 'outlineReview.importance.keyTopicDesc' },
];

// ─── Step indicator ─────────────────────────────────────────────────────────

const STEP_KEYS: readonly DictionaryKey[] = [
  'outlineReview.step.upload',
  'outlineReview.step.review',
  'outlineReview.step.generate',
  'outlineReview.step.study',
];

function StepIndicator({ current }: { current: number }) {
  const t = useT();
  const steps = STEP_KEYS.map((k) => t(k));
  return (
    <div className="or-steps">
      {steps.map((label, i) => (
        <div key={i} className={`or-step${i === current ? ' is-active' : ''}${i < current ? ' is-done' : ''}`}>
          <div className="or-step-dot">
            {i < current ? <CheckCircle2 size={16} /> : <span>{i + 1}</span>}
          </div>
          <span className="or-step-label">{label}</span>
          {i < steps.length - 1 && <div className="or-step-line" />}
        </div>
      ))}
    </div>
  );
}

// ─── Feedback modal ─────────────────────────────────────────────────────────

const FEEDBACK_OPTION_KEYS: Record<FeedbackIssue, DictionaryKey> = {
  missing_concept: 'outlineReview.feedbackMissingConcept',
  wrong_structure: 'outlineReview.feedbackWrongStructure',
  wrong_chapter: 'outlineReview.feedbackWrongChapter',
  other: 'outlineReview.feedbackOther',
};

function FeedbackPanel({
  onSubmit,
  onClose,
}: {
  onSubmit: (issue: FeedbackIssue, detail: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [selected, setSelected] = useState<FeedbackIssue | null>(null);
  const [detail, setDetail] = useState('');

  return (
    <div className="or-feedback">
      <h4 className="or-feedback-title">
        <AlertCircle size={16} />
        {t('outlineReview.feedbackTitle')}
      </h4>
      <div className="or-feedback-options">
        {(Object.keys(FEEDBACK_OPTION_KEYS) as FeedbackIssue[]).map((key) => (
          <label key={key} className={`or-feedback-opt${selected === key ? ' is-active' : ''}`}>
            <input
              type="radio"
              name="feedback-issue"
              checked={selected === key}
              onChange={() => setSelected(key)}
            />
            {t(FEEDBACK_OPTION_KEYS[key])}
          </label>
        ))}
      </div>
      {selected && (
        <textarea
          className="or-feedback-detail"
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          placeholder={t('outlineReview.feedbackPlaceholder')}
          rows={2}
          maxLength={500}
        />
      )}
      <div className="or-feedback-actions">
        <button type="button" className="or-feedback-cancel" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="or-feedback-submit"
          disabled={!selected}
          onClick={() => selected && onSubmit(selected, detail)}
        >
          {t('outlineReview.feedbackSubmit')}
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function OutlineReviewView({
  outline: initialOutline,
  courseTitle,
  onConfirm,
  onCancel,
  isConfirming,
}: OutlineReviewProps) {
  const t = useT();
  const toast = useToast();

  // ── Business logic — extracted hook (Session 48) ───────────────────────
  const {
    normalized, expanded, focusedIdx, showFeedback, feedbackSent,
    isValid, totalConcepts,
    toggleChapter, collapseAll, setFocusedIdx, setShowFeedback,
    updateChapter, deleteChapter, updateConcept,
    handleConfirm, handleFeedback,
  } = useOutlineEditor(initialOutline, onConfirm);

  // Keyboard navigation (already a hook from Session 16 — stays as glue)
  useOutlineKeyboard(true, {
    chapterCount: normalized.chapters.length,
    focusedIdx,
    setFocusedIdx,
    onToggleChapter: toggleChapter,
    getChapterId: (idx) => normalized.chapters[idx]!.id,
    onCollapseAll: collapseAll,
  });

  // Importance options resolved from the dictionary (19BM-D: was a zh/en
  // literal pair, now locale-agnostic — t() picks the active language).
  const impOptions = IMPORTANCE_KEYS.map(({ value, label, desc }) => ({
    value, label: t(label), desc: t(desc),
  }));

  // Labels handed to the ChapterCard presentational component. Filled from
  // the dictionary so the active locale drives every string. Interpolated
  // fields (subtitle / conceptsCount / deleteConfirm) use .replace() chains.
  const l: Labels = {
    title: t('outlineReview.title'),
    subtitle: t('outlineReview.subtitle', { course: courseTitle, chapters: normalized.chapters.length, concepts: totalConcepts }),
    note: t('outlineReview.note'),
    quickConfirm: t('outlineReview.quickConfirm'),
    quickConfirmHint: t('outlineReview.quickConfirmHint'),
    editConfirm: t('outlineReview.editConfirm'),
    confirming: t('outlineReview.confirming'),
    back: t('outlineReview.back'),
    chapter: t('outlineReview.chapter'),
    importance: t('outlineReview.importance'),
    concepts: t('outlineReview.concepts'),
    conceptsCount: (n: number) => t('outlineReview.conceptsCount', { count: n }),
    noteLabel: t('outlineReview.noteLabel'),
    notePlaceholder: t('outlineReview.notePlaceholder'),
    deleteLabel: t('outlineReview.deleteLabel'),
    deleteConfirm: (title: string) => t('outlineReview.deleteConfirm', { title }),
    invalid: t('outlineReview.invalid'),
    feedback: t('outlineReview.feedback'),
    feedbackSent: t('outlineReview.feedbackSent'),
    advanced: t('outlineReview.advanced'),
    advancedHint: t('outlineReview.advancedHint'),
    bloom: t('outlineReview.bloom'),
    bloomDesc: t('outlineReview.bloomDesc'),
    quota: t('outlineReview.quota'),
    quotaDesc: t('outlineReview.quotaDesc'),
    mustCover: t('outlineReview.mustCover'),
    mustCoverDesc: t('outlineReview.mustCoverDesc'),
    addConcept: t('outlineReview.addConcept'),
    addConceptPlaceholder: t('outlineReview.addConceptPlaceholder'),
  };

  return (
    <div className="or">
      {/* Step indicator */}
      <StepIndicator current={1} />

      {/* Header */}
      <div className="or-header">
        <div className="or-header-badge"><Pencil size={16} /></div>
        <h2 className="or-title">{l.title}</h2>
        <p className="or-subtitle">{l.subtitle}</p>
        <p className="or-note">{l.note}</p>
      </div>

      {/* Quick confirm — shortest path */}
      <div className="or-quick">
        <button
          type="button"
          className="or-quick-btn"
          onClick={handleConfirm}
          disabled={isConfirming}
        >
          <CheckCircle2 size={18} />
          <span className="or-quick-text">{l.quickConfirm}</span>
          <ArrowRight size={16} className="or-quick-arrow" />
        </button>
        <p className="or-quick-hint">{l.quickConfirmHint}</p>
      </div>

      {/* Chapter cards */}
      <div className="or-cards">
        {normalized.chapters.map((chapter, idx) => (
          <ChapterCard
            key={`${chapter.id}-${idx}`}
            chapter={chapter}
            idx={idx}
            impOptions={impOptions}
            labels={l}
            isExpanded={expanded.has(chapter.id)}
            isFocused={focusedIdx === idx}
            onToggle={() => toggleChapter(chapter.id)}
            onUpdate={(patch) => updateChapter(idx, patch)}
            onDelete={() => {
              // 19BM-C: chapter deletion is destructive. Sticky toast + action
              // replaces window.confirm.
              toast.warn(l.deleteConfirm(chapter.title), {
                sticky: true,
                action: { label: l.deleteLabel, onClick: () => { deleteChapter(idx); } },
              });
            }}
            onUpdateConcept={(cIdx, patch) => updateConcept(idx, cIdx, patch)}
          />
        ))}
      </div>

      {/* Bottom actions */}
      <div className="or-actions">
        <button type="button" className="or-back" onClick={onCancel} disabled={isConfirming}>
          {l.back}
        </button>
        <button
          type="button"
          className={`or-confirm${!isValid ? ' is-disabled' : ''}`}
          onClick={handleConfirm}
          disabled={!isValid || isConfirming}
        >
          {isConfirming ? l.confirming : l.editConfirm}
        </button>
      </div>

      {!isValid && <p className="or-invalid">{l.invalid}</p>}

      {/* Feedback */}
      <div className="or-feedback-trigger">
        {feedbackSent ? (
          <span className="or-feedback-sent"><CheckCircle2 size={14} /> {l.feedbackSent}</span>
        ) : (
          <button type="button" className="or-feedback-link" onClick={() => setShowFeedback(!showFeedback)}>
            <AlertCircle size={14} /> {l.feedback}
          </button>
        )}
      </div>
      {showFeedback && (
        <FeedbackPanel onSubmit={handleFeedback} onClose={() => setShowFeedback(false)} />
      )}
    </div>
  );
}
